import { useForm } from '@mantine/form';
import { noop } from 'lodash';
import { Box, Flex, Text, useMantineTheme } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import FormFooter from 'components/forms/FormFooter';
import { DeviceFaultFormRow } from 'components/forms/revision/fill-out/modals/device-fault-modal/DeviceFaultFormRow';
import { nanoid } from 'nanoid';
import { useCallback, useMemo } from 'react';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import { useFillOutRevisionDataProvider } from 'components/forms/revision/fill-out/data/FillOutRevisionDataProvider';
import Toast from 'components/Toast';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import Button from 'components/Button';

/**
 * The row data for the device fault form.
 */
export interface DeviceFault {
  id: string;
  fault: string;
  severity: string;
  description: string;
  moreInfo: string;
  context?: string;
  attachments?: {
    fileId: string;
    fileName: string;
    fileType: string;
  }[];
}

/**
 * The data collected from the form.
 */
export interface DeviceFaultFormData {
  faults: DeviceFault[];
}

/**
 * Parameters of the DeviceFaultForm component.
 */
export interface DeviceFaultFormProps {
  deviceTypeId: number;
  initialValues?: DeviceFaultFormData;
  onSubmit?: (values: DeviceFaultFormData) => void;
  onClose?: () => void;
}

/**
 * Form for collecting device fault data.
 */
export default function DeviceFaultForm({
  deviceTypeId,
  initialValues = { faults: [] },
  onSubmit = noop,
  onClose = noop,
}: DeviceFaultFormProps) {
  const { isTablet } = useResponsiveDesign();
  const theme = useMantineTheme();
  const { findInputByName, revision } = useFillOutRevisionDataProvider();

  const faultInput = useMemo(() => findInputByName('fault'), [findInputByName]);
  const severityInput = useMemo(() => findInputByName('severity'), [findInputByName]);
  const moreInfoInput = useMemo(() => findInputByName('moreInfo'), [findInputByName]);
  const observationInput = useMemo(() => findInputByName('observation'), [findInputByName]);
  const attachmentsInput = useMemo(() => findInputByName('attachments'), [findInputByName]);

  const form = useForm({
    initialValues,
    validate: {
      faults: {
        fault: createValidator(faultInput?.spec.required ? [required] : []),
        severity: createValidator(severityInput?.spec.required ? [required] : []),
        moreInfo: createValidator(moreInfoInput?.spec.required ? [required] : []),
      },
    },
  });

  /**
   * Adds a new fault entry to the form rows.
   */
  const addFault = useCallback(() => {
    form.insertListItem('faults', {
      id: nanoid(),
      fault: '',
      severity: '',
      description: '',
      moreInfo: '',
      attachments: [],
    });
  }, [form]);

  /**
   * Removes a fault entry from the form rows.
   */
  const removeFault = useCallback((i: number) => form.removeListItem('faults', i), [form]);

  if (!faultInput || !severityInput || !moreInfoInput || !attachmentsInput || !observationInput) {
    return (
      <Flex direction="column" justify="space-between" h="100%">
        <Box px={isTablet ? 20 : 40} pb={40}>
          <Toast
            type="fail"
            message="CHYBA: Nenašla sa sekcia pre nedostatky. Kontaktujte administrátora."
            fullWidth
            withCloseButton={false}
          />
        </Box>
        <FormFooter onSecondaryClick={onClose} skipSecondaryButtonConfirm pb={0} />
      </Flex>
    );
  }

  return (
    <form onSubmit={form.onSubmit(onSubmit)} style={{ height: '100%' }}>
      <Flex direction="column" justify="space-between" h="100%">
        <Box px={isTablet ? 20 : 40} pb={40}>
          <Flex bg="gray.0" style={{ borderTopLeftRadius: 4, borderTopRightRadius: 4 }} w="100%" align="flex-start">
            <Flex gap={24} py={24} px={16} align="flex-start" style={{ flex: '1 0 0' }}>
              {!faultInput.spec.hidden && (
                <Flex gap={8} style={{ flex: '1 1 0' }}>
                  <Text size="md" weight={500} color={theme.colors.gray[8]}>
                    {faultInput.spec.label} {!faultInput.spec.required && '(nepovinné)'}
                  </Text>
                </Flex>
              )}
              {!severityInput.spec.hidden && (
                <Flex gap={8} style={{ flex: '1 1 0' }}>
                  <Text size="md" weight={500} color={theme.colors.gray[8]}>
                    {severityInput.spec.label} {!severityInput.spec.required && '(nepovinné)'}
                  </Text>
                </Flex>
              )}
              {!moreInfoInput.spec.hidden && (
                <Flex gap={8} style={{ flex: '1 1 0' }}>
                  <Text size="md" weight={500} color={theme.colors.gray[8]}>
                    {moreInfoInput.spec.label} {!moreInfoInput.spec.required && '(nepovinné)'}
                  </Text>
                </Flex>
              )}
              {!observationInput.spec.hidden && (
                <Flex gap={8} style={{ flex: '1 1 0' }}>
                  <Text size="md" weight={500} color={theme.colors.gray[8]}>
                    {observationInput.spec.label} {!observationInput.spec.required && '(nepovinné)'}
                  </Text>
                </Flex>
              )}
              {!attachmentsInput.spec.hidden && (
                <Flex gap={8} style={{ flex: '1 1 0' }}>
                  <Text size="md" weight={500} color={theme.colors.gray[8]}>
                    {attachmentsInput.spec.label} {!attachmentsInput.spec.required && '(nepovinné)'}
                  </Text>
                </Flex>
              )}
            </Flex>

            <Box w={72} h={72} style={{ flexShrink: 0 }} />
          </Flex>

          {form.values.faults.map(({ id }, index) => (
            <DeviceFaultFormRow
              key={id}
              form={form}
              index={index}
              deviceTypeId={deviceTypeId}
              deviceTypeName={revision.deviceType.deviceTypeName}
              revisionId={revision.revisionId}
              faultInput={faultInput}
              severityInput={severityInput}
              moreInfoInput={moreInfoInput}
              observationInput={observationInput}
              attachmentsInput={attachmentsInput}
              onDelete={removeFault}
            />
          ))}

          <Button variant="subtle" leftIcon={<IconAlertTriangle />} mt={24} onClick={addFault}>
            Pridať nedostatok
          </Button>
        </Box>

        <FormFooter
          onSecondaryClick={onClose}
          closeConfirmTitle="Zrušiť zmeny v nedostatkoch"
          closeConfirmMessage="Naozaj chcete zrušiť zmeny v nedostatkoch? Všetky neuložené zmeny budú stratené."
          pb={0}
        />
      </Flex>
    </form>
  );
}
